<template>
  <form @submit.prevent="handleSubmit">
    <slot name="prepend" v-bind="slotProps" />

    <!-- Validation Summary -->
    <div
      v-if="showValidationSummary && Object.keys(errors).length > 0"
      class="mb-6 rounded-md border border-[--color-danger-200] bg-[--color-danger-50] p-4"
    >
      <h4 class="mb-2 font-medium text-[--color-danger-700]">
        {{ $t("arad.address_extension.validation.title") }}
      </h4>

      <ul class="list-inside list-disc space-y-1 text-sm text-[--color-danger-600]">
        <li v-for="(error, key) in errors" :key="key">{{ error }}</li>
      </ul>
    </div>

    <!-- 1. Business Info -->
    <div class="mb-6">
      <h3 class="mb-4 text-lg font-semibold">{{ $t("arad.address_extension.sections.business_info") }}</h3>

      <div class="mb-4 flex flex-col gap-4 md:flex-row">
        <div class="flex-1">
          <VcSelect
            v-model="form.guild"
            :items="guildItems"
            :label="$t('arad.address_extension.fields.guild')"
            :disabled="disabled"
            text-field="label"
            value-field="value"
            :error="!!errors.guild"
            required
            @focus="onInputFocus"
          />

          <p v-if="errors.guild" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.guild }}
          </p>
        </div>

        <div class="flex-1">
          <VcInput
            v-model="form.storeName"
            :label="$t('arad.address_extension.fields.store_name')"
            :disabled="disabled"
            :maxlength="128"
            :error="!!errors.storeName"
            :placeholder="$t('arad.address_extension.store_name.placeholder')"
            required
          />

          <p v-if="errors.storeName" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.storeName }}
          </p>
        </div>
      </div>

      <div class="mb-4 flex flex-col gap-4 md:flex-row">
        <div class="flex-1">
          <VcInput
            v-model="form.email"
            :label="$t('common.labels.email')"
            :disabled="disabled"
            :maxlength="64"
            :error="!!errors.email"
            :placeholder="$t('arad.address_extension.placeholders.email')"
          />

          <p v-if="errors.email" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.email }}
          </p>
        </div>

        <div class="flex-1">
          <VcInput
            v-model="form.phone"
            :label="$t('common.labels.phone')"
            :disabled="disabled"
            :maxlength="11"
            inputmode="numeric"
            :error="!!errors.phone"
            :placeholder="$t('arad.address_extension.placeholders.phone')"
            @input="filterDigitsOnly($event, 'phone')"
          />

          <p v-if="errors.phone" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.phone }}
          </p>
        </div>
      </div>
    </div>

    <!-- 2. Address -->
    <div class="mb-6">
      <h3 class="mb-4 text-lg font-semibold">{{ $t("arad.address_extension.sections.address_info") }}</h3>

      <VcSelect v-model="selectedCountry" text-field="name" :items="countries" class="hidden" />

      <div class="mb-4 flex flex-col gap-4 md:flex-row">
        <VcSelect
          v-model="selectedRegion"
          text-field="name"
          :items="regions"
          :label="$t('common.labels.region')"
          :disabled="disabled || !regions.length"
          class="flex-1"
          autocomplete
        />

        <div class="flex-1">
          <VcInput
            v-model="form.city"
            :label="$t('common.labels.city')"
            :disabled="disabled"
            :maxlength="128"
            :error="!!errors.city"
            :placeholder="$t('arad.address_extension.placeholders.city')"
            required
          />

          <p v-if="errors.city" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.city }}
          </p>
        </div>
      </div>

      <div class="mb-4">
        <VcInput
          v-model="form.line1"
          :label="$t('common.labels.address_line1')"
          :disabled="disabled"
          :maxlength="128"
          :error="!!errors.line1"
          :placeholder="$t('arad.address_extension.placeholders.address_line1')"
          required
        />

        <p v-if="errors.line1" class="mt-1 text-sm text-[--color-danger-500]">
          {{ errors.line1 }}
        </p>
      </div>

      <!-- پلاک،واحد and کد پستی - two columns on desktop -->
      <div class="mb-4 grid grid-cols-1 gap-4 md:grid-cols-2">
        <VcInput
          v-model="form.line2"
          :label="$t('common.labels.address_line2')"
          :disabled="disabled"
          :maxlength="128"
          :placeholder="$t('arad.address_extension.placeholders.address_line2')"
        />

        <div>
          <VcInput
            v-model="form.postalCode"
            :label="$t('common.labels.zip_or_postal_code')"
            :disabled="disabled"
            :maxlength="10"
            inputmode="numeric"
            :error="!!errors.postalCode"
            :placeholder="$t('arad.address_extension.placeholders.postal_code')"
            required
            @input="filterDigitsOnly($event, 'postalCode')"
          />

          <p v-if="errors.postalCode" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.postalCode }}
          </p>
        </div>
      </div>

      <!-- Location Picker - TEMPORARILY DISABLED, using static location -->
      <!-- TODO: Re-enable when map is ready
      <div class="mb-4">
        <label v-if="addressPurpose === 'shipping' || addressPurpose === 'both'" class="mb-2 block text-sm font-medium">
          {{ $t("arad.address_extension.sections.location") }}
          <span class="text-[--color-danger-500]">*</span>
        </label>

        <LocationPicker v-model="locationValue" :disabled="disabled" />

        <p v-if="errors.location" class="mt-1 text-sm text-[--color-danger-500]">
          {{ errors.location }}
        </p>
      </div>
      -->
    </div>

    <!-- 3. Billing Info -->
    <div class="mb-6">
      <h3 class="mb-4 text-lg font-semibold">{{ $t("arad.address_extension.sections.billing_info") }}</h3>

      <div class="mb-4">
        <label class="mb-2 block text-sm font-medium">
          {{ $t("arad.address_extension.fields.person_type") }}
          <span v-if="addressPurpose === 'billing' || addressPurpose === 'both'" class="text-[--color-danger-500]"
            >*</span
          >
        </label>

        <div class="flex gap-4">
          <VcRadioButton
            v-model="form.personType"
            value="natural"
            :disabled="disabled"
            :label="$t('arad.address_extension.person_types.natural')"
            name="person-type"
          />

          <VcRadioButton
            v-model="form.personType"
            value="legal"
            :disabled="disabled"
            :label="$t('arad.address_extension.person_types.legal')"
            name="person-type"
          />
        </div>

        <p v-if="errors.personType" class="mt-1 text-sm text-[--color-danger-500]">
          {{ errors.personType }}
        </p>
      </div>

      <!-- Natural Person -->
      <div v-if="form.personType === 'natural'" class="mt-4 space-y-4">
        <div>
          <VcInput
            v-model="form.naturalPersonName"
            :label="$t('arad.address_extension.natural_person.name')"
            :disabled="disabled"
            :maxlength="100"
            required
            :error="!!errors.naturalPersonName"
            :placeholder="$t('arad.address_extension.natural_person.name_placeholder')"
          />

          <p v-if="errors.naturalPersonName" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.naturalPersonName }}
          </p>
        </div>

        <div>
          <VcInput
            v-model="form.naturalPersonFamily"
            :label="$t('arad.address_extension.natural_person.family')"
            :disabled="disabled"
            :maxlength="100"
            required
            :error="!!errors.naturalPersonFamily"
            :placeholder="$t('arad.address_extension.natural_person.family_placeholder')"
          />

          <p v-if="errors.naturalPersonFamily" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.naturalPersonFamily }}
          </p>
        </div>

        <div>
          <VcInput
            v-model="form.naturalPersonNationalCode"
            :label="$t('arad.address_extension.natural_person.national_code')"
            :disabled="disabled"
            :maxlength="10"
            inputmode="numeric"
            required
            :error="!!errors.naturalPersonNationalCode"
            :placeholder="$t('arad.address_extension.natural_person.national_code_placeholder')"
            @input="filterDigitsOnly($event, 'naturalPersonNationalCode')"
          />

          <p v-if="errors.naturalPersonNationalCode" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.naturalPersonNationalCode }}
          </p>
        </div>
      </div>

      <!-- Legal Person -->
      <div v-if="form.personType === 'legal'" class="mt-4 space-y-4">
        <div>
          <VcInput
            v-model="form.legalPersonName"
            :label="$t('arad.address_extension.legal_person.name')"
            :disabled="disabled"
            :maxlength="256"
            required
            :error="!!errors.legalPersonName"
            :placeholder="$t('arad.address_extension.legal_person.name_placeholder')"
          />

          <p v-if="errors.legalPersonName" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.legalPersonName }}
          </p>
        </div>

        <div>
          <VcInput
            v-model="form.legalPersonNationalCode"
            :label="$t('arad.address_extension.legal_person.national_code')"
            :disabled="disabled"
            :maxlength="11"
            inputmode="numeric"
            required
            :error="!!errors.legalPersonNationalCode"
            :placeholder="$t('arad.address_extension.legal_person.national_code_placeholder')"
            @input="filterDigitsOnly($event, 'legalPersonNationalCode')"
          />

          <p v-if="errors.legalPersonNationalCode" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.legalPersonNationalCode }}
          </p>
        </div>

        <!-- Economical Code - TEMPORARILY DISABLED
        <div>
          <VcInput
            v-model="form.legalPersonEconomicalCode"
            :label="$t('arad.address_extension.legal_person.economical_code')"
            :disabled="disabled"
            :maxlength="12"
            inputmode="numeric"
            required
            :error="!!errors.legalPersonEconomicalCode"
            :placeholder="$t('arad.address_extension.legal_person.economical_code_placeholder')"
            @input="filterDigitsOnly($event, 'legalPersonEconomicalCode')"
          />

          <p v-if="errors.legalPersonEconomicalCode" class="mt-1 text-sm text-[--color-danger-500]">
            {{ errors.legalPersonEconomicalCode }}
          </p>
        </div>
        -->
      </div>

      <!-- File Uploads - Shown when any person type is selected (outside conditional blocks to prevent re-mounting) -->
      <div v-if="form.personType" class="mt-4 space-y-4">
        <!-- National Card Upload -->
        <PrivateFileUpload
          v-model:asset-id="form.nationalCardAssetId"
          :asset-type="PrivateAssetType.NationalCard"
          :label="`${$t('arad.address_extension.fields.national_card')} (${$t('arad.address_extension.fields.optional')})`"
          :error="errors.nationalCardAssetId"
          :disabled="disabled"
        />

        <!-- Business License Upload -->
        <PrivateFileUpload
          v-model:asset-id="form.businessLicenseAssetId"
          :asset-type="PrivateAssetType.BusinessLicense"
          :label="`${$t('arad.address_extension.fields.business_license')} (${$t('arad.address_extension.fields.optional')})`"
          :error="errors.businessLicenseAssetId"
          :disabled="disabled"
        />
      </div>
    </div>

    <slot name="append" v-bind="slotProps" />
  </form>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useInputValidation } from "@/arad/composables/useInputValidation";
import { useFormTracking } from "@/core/composables/useFormTracking";
import { getAddressName, Logger } from "@/core/utilities";
import { useGuilds } from "../composables";
import { PrivateAssetType } from "../types";
import LocationPicker from "./location-picker.vue";
import PrivateFileUpload from "./private-file-upload.vue";
import type { IAddressExtensionFormState, IGeoLocation } from "../types";
import type { CountryType, CountryRegionType, MemberAddressType } from "@/core/api/graphql/types";

const emit = defineEmits<IEmits>();

const props = withDefaults(defineProps<IProps>(), {
  countries: () => [],
  addressPurpose: "general",
});

interface IEmits {
  (event: "save", address: MemberAddressType, extension: IAddressExtensionFormState): void;
}

interface IProps {
  address?: MemberAddressType;
  extension?: IAddressExtensionFormState;
  countries?: CountryType[];
  disabled?: boolean;
  addressPurpose?: "shipping" | "billing" | "both" | "general";
}

let renderCount = 0;

// Show validation summary when user has interacted with the form
const showValidationSummary = ref(false);

const { t } = useI18n();

const { guilds, loadGuilds } = useGuilds();

const { onInputFocus, trackFormSubmitSuccess, trackFormSubmitError } = useFormTracking({
  formId: "extended_address",
  formName: "Extended Address Form",
});

// Expose addressPurpose to template
const addressPurpose = computed(() => props.addressPurpose);

// Validation helper functions
function isDigitsOnly(value: string): boolean {
  return /^\d*$/.test(value);
}

function validateIranianNationalCode(code: string): boolean {
  // Must be exactly 10 digits
  if (!/^\d{10}$/.test(code)) {
    return false;
  }

  // Check for invalid patterns (all same digits)
  if (/^(\d)\1{9}$/.test(code)) {
    return false;
  }

  // Iranian national code checksum validation
  const digits = code.split("").map(Number);
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += digits[i] * (10 - i);
  }
  const remainder = sum % 11;
  const checkDigit = digits[9];

  return (remainder < 2 && checkDigit === remainder) || (remainder >= 2 && checkDigit === 11 - remainder);
}

function validateCompanyNationalCode(code: string): boolean {
  // Must be exactly 11 digits
  if (!/^\d{11}$/.test(code)) {
    return false;
  }

  // Iranian company national code (شناسه ملی حقوقی) validation
  // Based on persian-tools algorithm: http://www.aliarash.com/article/shenasameli/shenasa_meli.htm

  // Control digit is the 11th digit (index 10)
  const controlDigit = parseInt(code.slice(10, 11));

  // d is based on 10th digit (index 9) + 2
  const d = parseInt(code.slice(9, 10)) + 2;

  // Coefficient weights (cycle through these)
  const z = [29, 27, 23, 19, 17];

  let sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += (d + parseInt(code.slice(i, i + 1))) * z[i % 5];
  }

  sum = sum % 11;
  if (sum === 10) {
    sum = 0;
  }

  return controlDigit === sum;
}

function validateEconomicalCode(code: string): boolean {
  // Must be exactly 12 digits
  return /^\d{12}$/.test(code);
}

function validatePostalCode(code: string): boolean {
  // Iranian postal code: exactly 10 digits, cannot start with 0
  return /^[1-9]\d{9}$/.test(code);
}

function validateEmail(email: string): boolean {
  // Standard email validation regex
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

const { sanitizeMobileNumber, normalizeDigits } = useInputValidation();

function validateIranianMobile(mobile: string): boolean {
  // Iranian mobile: starts with 09, exactly 11 digits (e.g., 09125248807)
  // First normalize Persian/Arabic digits
  const normalized = normalizeDigits(mobile);
  return /^09\d{9}$/.test(normalized);
}

// Filter input to only allow digits (but accept Persian/Arabic digits and convert them)
function filterDigitsOnly(event: Event, fieldName: keyof typeof form): void {
  const input = event.target as HTMLInputElement;
  // First normalize Persian/Arabic digits to English
  const normalized = normalizeDigits(input.value);
  // Then remove all non-digit characters
  const filtered = normalized.replace(/\D/g, "");
  if (filtered !== input.value) {
    input.value = filtered;
    (form as Record<string, unknown>)[fieldName] = filtered;
  }
}

// Simple reactive form - NO WATCHERS
const form = reactive({
  // Address fields
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  countryCode: "",
  countryName: "",
  regionId: "",
  regionName: "",
  city: "",
  line1: "",
  line2: "",
  postalCode: "",
  // Extension fields
  guild: "",
  storeName: "",
  personType: "" as string,
  naturalPersonName: "",
  naturalPersonFamily: "",
  naturalPersonNationalCode: "",
  nationalCardAssetId: null as string | null,
  businessLicenseAssetId: null as string | null,
  legalPersonName: "",
  legalPersonNationalCode: "",
  legalPersonEconomicalCode: "",
  // Static location - TODO: restore to null when LocationPicker is re-enabled
  latitude: 35.721469 as number,
  longitude: 51.346434 as number,
});

const originalValues = { address: "", extension: "" };

const guildItems = computed(() => {
  return guilds.value;
});

const regions = computed<CountryRegionType[]>(() => {
  return selectedCountry.value?.regions ?? [];
});

const selectedCountry = computed<CountryType | undefined>({
  get: () => {
    return props.countries.find((c) => c.id === form.countryCode);
  },
  set: (val) => {
    form.countryCode = val?.id ?? "";
    form.countryName = val?.name ?? "";
    form.regionId = "";
    form.regionName = "";
  },
});

const selectedRegion = computed<CountryRegionType | undefined>({
  get: () => regions.value.find((r) => r.id === form.regionId),
  set: (val) => {
    form.regionId = val?.id ?? "";
    form.regionName = val?.name ?? "";
    form.city = val?.id === "TE" ? "تهران" : "";
  },
});

// Static location constants - TODO: remove when LocationPicker is re-enabled
const STATIC_LATITUDE = 35.721469;
const STATIC_LONGITUDE = 51.346434;

const locationValue = computed<IGeoLocation | null>({
  get: () => {
    if (form.latitude !== null && form.longitude !== null) {
      return { latitude: form.latitude, longitude: form.longitude };
    }
    return null;
  },
  set: (val) => {
    // Use static fallback when LocationPicker is disabled
    form.latitude = val?.latitude ?? STATIC_LATITUDE;
    form.longitude = val?.longitude ?? STATIC_LONGITUDE;
  },
});

const isDirty = computed(() => {
  renderCount++;
  const currentAddress = JSON.stringify({
    email: form.email,
    phone: form.phone,
    countryCode: form.countryCode,
    regionId: form.regionId,
    city: form.city,
    line1: form.line1,
    line2: form.line2,
    postalCode: form.postalCode,
  });
  const currentExtension = JSON.stringify({
    guild: form.guild,
    storeName: form.storeName,
    personType: form.personType,
    naturalPersonName: form.naturalPersonName,
    naturalPersonFamily: form.naturalPersonFamily,
    naturalPersonNationalCode: form.naturalPersonNationalCode,
    legalPersonName: form.legalPersonName,
    legalPersonNationalCode: form.legalPersonNationalCode,
    legalPersonEconomicalCode: form.legalPersonEconomicalCode,
    latitude: form.latitude,
    longitude: form.longitude,
  });
  return currentAddress !== originalValues.address || currentExtension !== originalValues.extension;
});

// Validation errors
const errors = computed(() => {
  const result: Record<string, string> = {};

  // Basic required fields
  if (!form.guild) {
    result.guild = t("arad.address_extension.validation.guild_required");
  }
  if (!form.storeName) {
    result.storeName = t("arad.address_extension.validation.store_name_required");
  }

  // Email validation (optional but must be valid if provided)
  if (form.email && !validateEmail(form.email)) {
    result.email = t("arad.address_extension.validation.email_invalid");
  }

  // Iranian mobile validation (optional but must be valid if provided)
  if (form.phone && !validateIranianMobile(form.phone)) {
    result.phone = t("arad.address_extension.validation.phone_invalid");
  }
  if (!form.city) {
    result.city = t("arad.address_extension.validation.city_required");
  }
  if (!form.line1) {
    result.line1 = t("arad.address_extension.validation.address_required");
  }

  // Postal code validation
  if (!form.postalCode) {
    result.postalCode = t("arad.address_extension.validation.postal_code_required");
  } else if (!validatePostalCode(form.postalCode)) {
    result.postalCode = t("arad.address_extension.validation.postal_code_invalid");
  }

  // Shipping or Both: Location is mandatory - TEMPORARILY DISABLED (using static location)
  // if (props.addressPurpose === "shipping" || props.addressPurpose === "both") {
  //   if (form.latitude === null || form.longitude === null) {
  //     result.location = t("arad.address_extension.validation.location_required_for_shipping");
  //   }
  // }

  // Billing or Both: Person type selection is mandatory
  if ((props.addressPurpose === "billing" || props.addressPurpose === "both") && !form.personType) {
    result.personType = t("arad.address_extension.validation.person_type_required_for_billing");
  }

  // If person type is selected, related fields are mandatory (any address purpose)
  if (form.personType === "natural") {
    if (!form.naturalPersonName) {
      result.naturalPersonName = t("arad.address_extension.validation.natural_person_name_required");
    }
    if (!form.naturalPersonFamily) {
      result.naturalPersonFamily = t("arad.address_extension.validation.natural_person_family_required");
    }
    // National code validation for natural person
    if (!form.naturalPersonNationalCode) {
      result.naturalPersonNationalCode = t("arad.address_extension.validation.national_code_required");
    } else if (!validateIranianNationalCode(form.naturalPersonNationalCode)) {
      result.naturalPersonNationalCode = t("arad.address_extension.validation.national_code_invalid");
    }
    // File uploads are optional - nationalCardAssetId and businessLicenseAssetId not required
  } else if (form.personType === "legal") {
    if (!form.legalPersonName) {
      result.legalPersonName = t("arad.address_extension.validation.legal_person_name_required");
    }
    // National code validation for legal person
    if (!form.legalPersonNationalCode) {
      result.legalPersonNationalCode = t("arad.address_extension.validation.legal_national_code_required");
    } else if (!validateCompanyNationalCode(form.legalPersonNationalCode)) {
      result.legalPersonNationalCode = t("arad.address_extension.validation.legal_national_code_invalid");
    }
    // Economical code validation - TEMPORARILY DISABLED
    // if (!form.legalPersonEconomicalCode) {
    //   result.legalPersonEconomicalCode = t("arad.address_extension.validation.economical_code_required");
    // } else if (!validateEconomicalCode(form.legalPersonEconomicalCode)) {
    //   result.legalPersonEconomicalCode = t("arad.address_extension.validation.economical_code_invalid");
    // }
    // File uploads are optional - nationalCardAssetId and businessLicenseAssetId not required
  }

  return result;
});

const isValid = computed(() => {
  return Object.keys(errors.value).length === 0;
});

const slotProps = computed(() => ({
  dirty: isDirty.value,
  valid: isValid.value,
  errors: errors.value,
}));

// Show validation summary when form is dirty and has errors
watch(isDirty, (dirty) => {
  if (dirty) {
    showValidationSummary.value = true;
  }
});

// Initialize ONCE on mount - load guilds only
onMounted(async () => {
  try {
    await loadGuilds();
  } catch (error) {
    Logger.error("ExtendedAddressForm", "Failed to load guilds:", error);
  }
});

// Helper to update original values (for dirty check)
function updateOriginalValues(): void {
  originalValues.address = JSON.stringify({
    email: form.email,
    phone: form.phone,
    countryCode: form.countryCode,
    regionId: form.regionId,
    city: form.city,
    line1: form.line1,
    line2: form.line2,
    postalCode: form.postalCode,
  });
  originalValues.extension = JSON.stringify({
    guild: form.guild,
    storeName: form.storeName,
    personType: form.personType,
    naturalPersonName: form.naturalPersonName,
    naturalPersonFamily: form.naturalPersonFamily,
    naturalPersonNationalCode: form.naturalPersonNationalCode,
    nationalCardAssetId: form.nationalCardAssetId,
    businessLicenseAssetId: form.businessLicenseAssetId,
    legalPersonName: form.legalPersonName,
    legalPersonNationalCode: form.legalPersonNationalCode,
    legalPersonEconomicalCode: form.legalPersonEconomicalCode,
    latitude: form.latitude,
    longitude: form.longitude,
  });
}

// Watch for countries prop changes (loads async after onMounted)
watch(
  () => props.countries,
  (newCountries) => {
    // Auto-select Iran when countries load and no country is selected
    if (newCountries.length > 0 && !form.countryCode) {
      const iran = newCountries.find((c) => c.id === "IRN" || c.id === "IR" || c.name?.toLowerCase().includes("iran"));
      if (iran) {
        form.countryCode = iran.id;
        form.countryName = iran.name ?? "";
        updateOriginalValues();
      }
    }
  },
  { immediate: true },
);

// Watch for regions changes (loads after country is selected)
watch(
  regions,
  (newRegions) => {
    // Auto-select Tehran when regions load and no region is selected
    if (newRegions.length > 0 && !form.regionId && !props.address?.id) {
      const tehran = newRegions.find((r) => r.id === "TE" || r.name === "تهران");
      if (tehran) {
        form.regionId = tehran.id;
        form.regionName = tehran.name ?? "";
        form.city = "تهران";
        updateOriginalValues();
      }
    }
  },
  { immediate: true },
);

// Watch for address prop changes (loads async after onMounted)
watch(
  () => props.address,
  (newAddress) => {
    if (newAddress) {
      form.firstName = newAddress.firstName ?? "";
      form.lastName = newAddress.lastName ?? "";
      form.email = newAddress.email ?? "";
      form.phone = newAddress.phone ?? "";
      form.countryCode = newAddress.countryCode ?? "";
      form.countryName = newAddress.countryName ?? "";
      form.regionId = newAddress.regionId ?? "";
      form.regionName = newAddress.regionName ?? "";
      form.city = newAddress.city ?? "";
      form.line1 = newAddress.line1 ?? "";
      form.line2 = newAddress.line2 ?? "";
      form.postalCode = newAddress.postalCode ?? "";
      updateOriginalValues();
    }
  },
  { immediate: true },
);

// Watch for extension prop changes (loads async after onMounted)
watch(
  () => props.extension,
  (newExtension) => {
    if (newExtension) {
      form.guild = newExtension.guild ?? "";
      form.storeName = newExtension.storeName ?? "";
      form.personType = newExtension.personType ?? "";
      form.naturalPersonName = newExtension.naturalPersonName ?? "";
      form.naturalPersonFamily = newExtension.naturalPersonFamily ?? "";
      form.naturalPersonNationalCode = newExtension.naturalPersonNationalCode ?? "";
      form.nationalCardAssetId = newExtension.nationalCardAssetId ?? null;
      form.businessLicenseAssetId = newExtension.businessLicenseAssetId ?? null;
      form.legalPersonName = newExtension.legalPersonName ?? "";
      form.legalPersonNationalCode = newExtension.legalPersonNationalCode ?? "";
      form.legalPersonEconomicalCode = newExtension.legalPersonEconomicalCode ?? "";
      // Use static fallback if extension has null lat/lng (LocationPicker is disabled)
      form.latitude = newExtension.latitude ?? STATIC_LATITUDE;
      form.longitude = newExtension.longitude ?? STATIC_LONGITUDE;
      updateOriginalValues();
    }
  },
  { immediate: true },
);
function handleSubmit(): void {
  // Validate form before submitting
  if (Object.keys(errors.value).length > 0) {
    showValidationSummary.value = true;
    trackFormSubmitError("Validation failed", Object.keys(errors.value));
    return;
  }

  const address: MemberAddressType = {
    id: props.address?.id,
    isDefault: props.address?.isDefault ?? false,
    isFavorite: props.address?.isFavorite ?? false,
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    phone: form.phone,
    countryCode: form.countryCode,
    countryName: form.countryName,
    regionId: form.regionId,
    regionName: form.regionName,
    city: form.city,
    line1: form.line1,
    line2: form.line2,
    postalCode: form.postalCode,
    organization: form.storeName,
    name: "",
  };
  address.name = getAddressName(address);

  const extension: IAddressExtensionFormState = {
    guild: form.guild,
    storeName: form.storeName,
    personType: form.personType as IAddressExtensionFormState["personType"],
    naturalPersonName: form.naturalPersonName,
    naturalPersonFamily: form.naturalPersonFamily,
    naturalPersonNationalCode: form.naturalPersonNationalCode,
    nationalCardAssetId: form.nationalCardAssetId,
    legalPersonName: form.legalPersonName,
    legalPersonNationalCode: form.legalPersonNationalCode,
    legalPersonEconomicalCode: form.legalPersonEconomicalCode,
    businessLicenseAssetId: form.businessLicenseAssetId,
    // Ensure static values are always submitted (LocationPicker is disabled)
    latitude: form.latitude ?? STATIC_LATITUDE,
    longitude: form.longitude ?? STATIC_LONGITUDE,
  };

  trackFormSubmitSuccess();
  emit("save", address, extension);
}
</script>
